import { Fragment, useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Polygon, Polyline, CircleMarker, Popup, useMap } from 'react-leaflet';
import MapTouchGate from '../map/MapTouchGate';
import type { LocationRecord, CoverageResult, JobTarget } from '../../types/jobs';
import type { GeoJSONPolygon } from '../../types/orchard';
import orchardService from '../../services/orchard';
import { TILE_LAYERS, MAP_MAX_ZOOM, type TileLayerKey } from '../../utils/leafletGeo';
import LayerToggleControl from '../map/LayerToggleControl';
import 'leaflet/dist/leaflet.css';

interface JobCoverageMapProps {
  /** Boundary snapshot taken when the job was created (Polygon or FeatureCollection). */
  boundaryGeometry?: any | null;
  /** Job targets. Used to name/colour the snapshot, and to fetch live boundaries when it has none. */
  targets?: JobTarget[];
  waypoints?: LocationRecord[];
  coverage?: CoverageResult | null;
  height?: string;
}

// Color palette for different operators
const OPERATOR_COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

// Boundary colors by target type (matches orchard map convention)
const BOUNDARY_COLOR: Record<string, string> = {
  farm: '#22c55e',
  block: '#3b82f6',
  cultivar_area: '#8b5cf6',
  default: '#4f46e5',
};

type LatLng = [number, number];

interface ParsedBoundary {
  key: string;
  positions: LatLng[];
  name?: string;
  targetType?: string;
}

const toLatLng = (coord: number[]): LatLng => [coord[1], coord[0]];

function ringToPositions(ring: unknown): LatLng[] {
  if (!Array.isArray(ring)) return [];
  return ring
    .filter(
      (c): c is number[] =>
        Array.isArray(c) && c.length >= 2 && Number.isFinite(c[0]) && Number.isFinite(c[1])
    )
    .map(toLatLng);
}

/**
 * Parse job boundary_geometry into renderable polygons.
 * Handles:
 *   - GeoJSON Polygon (single target)
 *   - GeoJSON FeatureCollection<Polygon> (multiple targets, stored by resolveTargets())
 */
function parseBoundaryPolygons(geometry: any): ParsedBoundary[] {
  if (!geometry) return [];

  if (geometry.type === 'Polygon') {
    const positions = ringToPositions(geometry.coordinates?.[0]);
    return positions.length >= 3 ? [{ key: 'boundary', positions }] : [];
  }

  if (geometry.type === 'FeatureCollection') {
    const results: ParsedBoundary[] = [];
    (geometry.features ?? []).forEach((feature: any, idx: number) => {
      const g = feature?.geometry;
      if (g?.type !== 'Polygon') return;
      const positions = ringToPositions(g.coordinates?.[0]);
      if (positions.length < 3) return;
      results.push({
        key: `${feature.properties?.target_type ?? 'feature'}-${feature.properties?.target_id ?? idx}`,
        positions,
        name: feature.properties?.name,
        targetType: feature.properties?.target_type,
      });
    });
    return results;
  }

  return [];
}

function waypointToLatLng(wp: LocationRecord): LatLng | null {
  const lat = parseFloat(wp.latitude);
  const lng = parseFloat(wp.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return [lat, lng];
}

function FitBounds({ positions }: { positions: LatLng[] }) {
  const map = useMap();
  // Refit only when the data actually changes — `positions` is a fresh array on
  // every render, and refitting on each one fights the operator's own panning.
  const signature = positions.length
    ? `${positions.length}:${positions[0][0]},${positions[0][1]}:${positions[positions.length - 1][0]},${positions[positions.length - 1][1]}`
    : 'empty';

  useEffect(() => {
    // No animation: the fit runs as the job's data arrives, and an animated
    // zoom that is still in flight when the page changes leaves Leaflet
    // finishing a transition on a map that no longer exists.
    if (positions.length > 1) {
      map.fitBounds(positions, { padding: [30, 30], animate: false });
    } else if (positions.length === 1) {
      map.setView(positions[0], 18, { animate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, signature]);
  return null;
}

/**
 * JobCoverageMap renders the job's target boundaries and each operator's GPS
 * trail over satellite imagery.
 *
 * Satellite is the default base layer on purpose: an orchard block on OSM's
 * street tiles is a featureless white square, so the polygon and trail float
 * over nothing and the map reads as broken.
 *
 * Boundaries come from the job's snapshot when it has one. When it doesn't —
 * a target whose polygon was drawn after the job was created — they are
 * fetched live from the orchard API, so the map still shows the block.
 */
function JobCoverageMap({
  boundaryGeometry,
  targets = [],
  waypoints = [],
  coverage,
  height = '400px',
}: JobCoverageMapProps) {
  const [fetchedBoundaries, setFetchedBoundaries] = useState<ParsedBoundary[]>([]);
  const [loadingBoundaries, setLoadingBoundaries] = useState(false);
  const [mapLayer, setMapLayer] = useState<TileLayerKey>('satellite');

  // Boundaries carried by the job itself.
  const snapshotBoundaries = useMemo(() => {
    const parsed = parseBoundaryPolygons(boundaryGeometry);
    // A single-target job stores a bare Polygon with no properties — name and
    // colour it from the target the job already knows about.
    if (parsed.length === 1 && !parsed[0].targetType && targets.length === 1) {
      return [
        {
          ...parsed[0],
          name: targets[0].target_name ?? undefined,
          targetType: targets[0].target_type,
        },
      ];
    }
    return parsed;
  }, [boundaryGeometry, targets]);

  // Stable key so the fetch effect doesn't re-run on every parent render.
  const targetKey = useMemo(
    () => targets.map((t) => `${t.target_type}-${t.target_id}`).join(','),
    [targets]
  );

  // --- Fall back to live orchard boundaries when the snapshot has none --------
  useEffect(() => {
    if (snapshotBoundaries.length || !targetKey) {
      setFetchedBoundaries((prev) => (prev.length ? [] : prev));
      return;
    }

    let cancelled = false;
    setLoadingBoundaries(true);

    (async () => {
      const results = await Promise.all(
        targets.map(async (target): Promise<ParsedBoundary | null> => {
          try {
            const res =
              target.target_type === 'farm'
                ? await orchardService.getFarm(target.target_id)
                : target.target_type === 'block'
                  ? await orchardService.getBlock(target.target_id)
                  : await orchardService.getCultivarArea(target.target_id);

            if (!res.success || !res.data) return null;
            const geometry = res.data.boundary_geometry as GeoJSONPolygon | null;
            const positions = ringToPositions(geometry?.coordinates?.[0]);
            if (positions.length < 3) return null;

            return {
              key: `${target.target_type}-${target.target_id}`,
              positions,
              name: target.target_name ?? undefined,
              targetType: target.target_type,
            };
          } catch {
            return null; // one missing boundary shouldn't blank the whole map
          }
        })
      );

      if (cancelled) return;
      setFetchedBoundaries(results.filter((b): b is ParsedBoundary => b !== null));
      setLoadingBoundaries(false);
    })();

    return () => {
      cancelled = true;
      setLoadingBoundaries(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey, snapshotBoundaries.length]);

  const boundaries = snapshotBoundaries.length ? snapshotBoundaries : fetchedBoundaries;

  // Group waypoints by user_id for multi-operator coloring
  const trailsByUser = useMemo(() => {
    const byUser = new Map<number, LatLng[]>();
    for (const wp of waypoints) {
      const position = waypointToLatLng(wp);
      if (!position) continue;
      const existing = byUser.get(wp.user_id);
      if (existing) existing.push(position);
      else byUser.set(wp.user_id, [position]);
    }
    return byUser;
  }, [waypoints]);

  // All positions for bounds fitting
  const allPositions: LatLng[] = useMemo(
    () => [
      ...boundaries.flatMap((b) => b.positions),
      ...Array.from(trailsByUser.values()).flat(),
    ],
    [boundaries, trailsByUser]
  );

  const center: LatLng = useMemo(() => {
    if (!allPositions.length) return [-37.8, 145.0];
    return [
      allPositions.reduce((s, p) => s + p[0], 0) / allPositions.length,
      allPositions.reduce((s, p) => s + p[1], 0) / allPositions.length,
    ];
  }, [allPositions]);

  const coveragePct = coverage?.covered_pct;
  const coverageColor = coveragePct != null
    ? (coveragePct >= 90 ? 'text-green-600' : coveragePct >= 70 ? 'text-amber-600' : 'text-red-600')
    : 'text-neutral-500';

  if (!allPositions.length) {
    return (
      <div
        style={{ height }}
        className="bg-neutral-100 rounded-lg border border-neutral-200 flex items-center justify-center px-4 text-center text-sm text-neutral-400"
      >
        {loadingBoundaries
          ? 'Loading map…'
          : 'No mapped boundary or GPS track for this job yet.'}
      </div>
    );
  }

  const tiles = TILE_LAYERS[mapLayer];

  return (
    <div className="rounded-lg overflow-hidden border border-neutral-200">
      {/* Coverage legend */}
      {coveragePct != null && (
        <div className="bg-white px-4 py-2 flex items-center justify-between border-b border-neutral-100">
          <div className="text-sm text-neutral-600">
            Coverage
            {coverage?.covered_area_ha != null && coverage?.target_area_ha != null && (
              <span className="text-neutral-400 ml-2 text-xs">
                {coverage.covered_area_ha.toFixed(2)} / {coverage.target_area_ha.toFixed(1)} ha
              </span>
            )}
          </div>
          <span className={`text-lg font-bold ${coverageColor}`}>{coveragePct.toFixed(1)}%</span>
        </div>
      )}

      <MapContainer
        center={center}
        zoom={16}
        maxZoom={MAP_MAX_ZOOM}
        style={{ height, width: '100%' }}
        className="z-0"
        scrollWheelZoom={true}
      >
        <TileLayer
          key={mapLayer}
          attribution={tiles.attribution}
          url={tiles.url}
          maxNativeZoom={tiles.maxNativeZoom}
          maxZoom={tiles.maxZoom}
        />

        <LayerToggleControl
          mapLayer={mapLayer}
          onToggle={() => setMapLayer((l) => (l === 'street' ? 'satellite' : 'street'))}
        />

        <FitBounds positions={allPositions} />

        {/* Target boundaries — one polygon per target */}
        {boundaries.map((boundary) => {
          const color = BOUNDARY_COLOR[boundary.targetType ?? ''] ?? BOUNDARY_COLOR.default;
          return (
            <Polygon
              key={boundary.key}
              positions={boundary.positions}
              pathOptions={{ color, weight: 2, dashArray: '8 4', fillOpacity: 0.08 }}
            >
              {boundary.name && <Popup>{boundary.name}</Popup>}
            </Polygon>
          );
        })}

        {/* Waypoint trails per operator */}
        {Array.from(trailsByUser.entries()).map(([userId, positions], idx) => {
          const color = OPERATOR_COLORS[idx % OPERATOR_COLORS.length];

          return (
            <Fragment key={userId}>
              <Polyline positions={positions} pathOptions={{ color, weight: 3, opacity: 0.8 }} />
              {/* Start marker */}
              {positions.length > 0 && (
                <CircleMarker center={positions[0]} radius={6} pathOptions={{ color, fillColor: '#fff', fillOpacity: 1, weight: 2 }}>
                  <Popup>Start</Popup>
                </CircleMarker>
              )}
              {/* End/current marker */}
              {positions.length > 1 && (
                <CircleMarker center={positions[positions.length - 1]} radius={6} pathOptions={{ color, fillColor: color, fillOpacity: 1, weight: 2 }}>
                  <Popup>Latest position</Popup>
                </CircleMarker>
              )}
            </Fragment>
          );
        })}

        <MapTouchGate />
      </MapContainer>
    </div>
  );
}

export default JobCoverageMap;
